import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable + creatable combobox.
 *
 * Props:
 *  - value: current selected value (string)
 *  - onValueChange: fn(value) called when user selects
 *  - items: [{ value, label }]
 *  - placeholder: trigger placeholder when no value selected
 *  - searchPlaceholder
 *  - emptyMessage
 *  - onCreate: async fn(label) => { value, label } — when provided, shows "Create '{x}'" row
 *  - disabled
 *  - triggerClassName
 *  - testid
 */
export function Combobox({
  value,
  onValueChange,
  items,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  onCreate,
  disabled = false,
  triggerClassName,
  testid,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const selected = items.find((i) => i.value === value);
  const queryTrimmed = query.trim();
  const showCreate =
    !!onCreate &&
    queryTrimmed.length > 0 &&
    !items.some((i) => i.label.toLowerCase() === queryTrimmed.toLowerCase());

  const handleCreate = async () => {
    if (!onCreate || !queryTrimmed) return;
    setCreating(true);
    try {
      const created = await onCreate(queryTrimmed);
      if (created?.value) {
        onValueChange(created.value);
        setOpen(false);
        setQuery("");
      }
    } finally {
      setCreating(false);
    }
  };

  const filteredItems = React.useMemo(() => {
    if (!queryTrimmed) return items;
    const q = queryTrimmed.toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, queryTrimmed]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full justify-between rounded-xl bg-card font-normal",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-2xl"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={testid ? `${testid}-search` : undefined}
          />
          <CommandList>
            {filteredItems.length === 0 && !showCreate && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            {filteredItems.length > 0 && (
              <CommandGroup>
                {filteredItems.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.label}
                    onSelect={() => {
                      onValueChange(item.value);
                      setOpen(false);
                      setQuery("");
                    }}
                    data-testid={testid ? `${testid}-option-${item.value}` : undefined}
                    className="rounded-lg"
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === item.value ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{item.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create__${queryTrimmed}`}
                  onSelect={handleCreate}
                  disabled={creating}
                  data-testid={testid ? `${testid}-create` : undefined}
                  className="rounded-lg text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {creating ? "Creating…" : (<>Create &ldquo;<span className="font-medium">{queryTrimmed}</span>&rdquo;</>)}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
